import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NgOptimizedImage, NgTemplateOutlet } from "@angular/common";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatButtonModule } from "@angular/material/button";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from "@angular/router";
import { filter, map, startWith } from "rxjs";

import { AuthService } from "../../auth/auth.service";

@Component({
    selector: "app-navbar",
    templateUrl: "./navbar.component.html",
    styleUrls: ["./navbar.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgOptimizedImage,
        NgTemplateOutlet,
        MatToolbarModule,
        MatButtonModule,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        RouterLink
    ]
})
export class NavbarComponent {
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);

    readonly producerLink = input<string[]>(["/interactives/producer"]);
    readonly hostLink = input<string[]>(["/interactives/host"]);

    private readonly childData = toSignal(
        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            startWith(null),
            map(() => this.activatedRoute.snapshot.firstChild?.data ?? {})
        )
    );

    readonly navTitle = () => this.childData()?.["navTitle"] as string | undefined;
    readonly navIcon = () => this.childData()?.["navIcon"] as string | undefined;

    readonly isAuthenticated = this.authService.isAuthenticated;
    private readonly isAdmin = this.authService.isAdmin;
    private readonly isProducer = this.authService.isProducer;
    private readonly isMarketer = this.authService.isMarketer;
    readonly isHost = this.authService.isHost;

    readonly hasAccessToInteractives = computed(() => this.isAdmin() || this.isProducer() || this.isMarketer());
    readonly hasAccessToProducerDashboard = computed(() => this.isAdmin() || this.isProducer() || this.isMarketer());
    // Hosts can reach the host view, but it is already their home route, so the link would be redundant.
    readonly showHostViewLink = computed(() => this.isAdmin() || this.isProducer());
    readonly hasAccessToAdminPanel = this.isAdmin;

    readonly displayName = toSignal(this.authService.getCurrentUserDisplayName());

    signOut(): void {
        this.authService.logout().subscribe({
            next: () => void this.router.navigate(["/login"])
        });
    }
}
